/* eslint-disable max-len */
import React from 'react';
import styled from 'styled-components';
import { SectionName, SectionTitle } from '../common/styles';

const Container = styled.div`
  margin: ${props => (props.theme.isMobile ? '67px 0 19px 19px' : '0 auto')};
  padding-bottom: 6px;
  max-width: 1230px;
  width: 85vw;
  display: flex;
  flex-direction: column;
  align-items: start;
`;

export const Timestamp = styled(SectionName)`
  font-size: 12px;
`;

export const Title = styled(SectionTitle)`
  margin: 6px 0 0 0;
  border-bottom: 1px dashed ${props => props.theme.primaryColor};
  font-size: ${props => (props.theme.isMobile ? 25 : 40)}px;
`;

export const Section = styled.div`
  max-width: 1180px;
  width: 82vw;
  font-size: 16px;
  margin-top: 10px;
  display: flex;
  flex-direction: row;
  align-items: start;
  line-height: 25px;
`;

export const InnerSection = styled(Section)`
  max-width: 1120px;
`;

export const Paragraph = styled.p`
  position: relative;
  max-width: 449px;
  margin-top: 24px;
  color: ${props => props.theme.bodyColor};
  font-size: 20px;
  line-height: 25px;
  text-align: left;
`;

export const Intro = styled(Paragraph)`
  max-width: 1180px;
  font-size: 16px;
  margin-top: 44px;
`;

export const Headline = styled(Paragraph)`
  max-width: 1180px;
  width: 82vw;
  font-size: 20px;
  font-weight: ${props => props.theme.fontWeightBold};
  margin-top: 33px;
`;

export const SubHeadline = styled(Paragraph)`
  max-width: 1180px;
  width: 82vw;
  font-size: 18px;
`;

const Span10 = styled.span`
  margin-right: 10px;
`;

const Underscore = styled.span`
  text-decoration: underline;
`;

export default function Content() {
  return (
    <Container>
      <Title>
        General Terms - Advertising - Market Index
      </Title>

      <Headline>1. Advertising Services</Headline>
      <Section>
        <span>Subject to the other terms of this agreement, we will provide to you (Partner) the Advertising Services described in the Order Form. The Partner accepts the Order Form by: signing it (including electronically); confirming in writing (including email) that it accepts it; asking us in writing (including email) to provide the services set out in the Order Form. </span>
      </Section>
      <Section>
        <span>
          Each time the Partner accepts an Order Form, it creates a separate binding agreement (
          <strong>Agreement</strong>
          ) comprising that Order Form and these Terms. If there is a conflict between these documents, the Order Form prevails.
        </span>
      </Section>
      <Section>
        <span>You may request future Advertising Services and, where we both agree in writing (which may be by email) on the scope of those additional services and the fees, then we both agree that those additional services are also provided on these Terms without the need for us to prepare a formal Services Schedule.</span>
      </Section>
      <Section>
        <span>You agree to provide the proposed Partner Content for the advertisement; and to comply with the advertising restrictions in Schedule 1.</span>
      </Section>
      <Section>
        <span>Advertising submitted electronically must comply with our specifications. We may reject the Partner Content if it is not submitted in accordance with such specifications.</span>
      </Section>
      <Section>
        <span>You must promptly check any proofs of Partner Content, if we have arranged to provide you with any and notify us of any errors in the proofs or in any Partner Content that we publish for you. We do not accept any responsibility for errors in Partner Content that has been submitted electronically by you.</span>
      </Section>
      <Section>
        <span>We will review the Partner Content and, if we have any concerns about publishing it, we will promptly notify you and work in good faith to resolve any concerns. We retain ultimate discretion whether to publish any Partner Content and if so, its positioning and placement. We will not charge you fees for Partner Content that we do not publish.</span>
      </Section>
      <Section>
        <span>We may ask to amend Partner Content if we believe it is necessary to comply with these Terms, or any law, pre-existing agreement we have with a third party or a third party’s Intellectual Property Rights. If we amend your Partner Content, this will not reduce the Fee. We will notify you of any changes we need to make, but we will not be liable for any costs, expenses, losses or damages suffered or incurred by you arising from us amending or not publishing your Partner Content. We may mark any Partner Content as “Advertisement” whenever required by law or if we consider it appropriate, to distinguish it from other types of Content.</span>
      </Section>
      <Section>
        <span>You must not resell advertising space to any third party without our consent.</span>
      </Section>
      <Headline>2. Fees & Cancellations</Headline>
      <SubHeadline><strong>2.1 Fees</strong></SubHeadline>
      <Section>
        <span>You agree to pay the fees for the supply of the Advertising Services as described in the Order Form within 14 days of our invoice date. Once paid, Fees are not refundable. Invoices will be issued at the time of activity booking. Invoice dates will be set to the first day of activity. Invoices will be due 14 days from the invoice date. Invoices will be increased to take account of any taxes, including GST in Australia. If your payment is overdue, we may stop providing Advertising Services.</span>
      </Section>
      <SubHeadline><strong>2.2 Cancellation</strong></SubHeadline>
      <Section>
        <span>You may cancel the Advertising Services by giving us written notice (which may be email) no less than 60 days before the scheduled publication date in the Order Form. This will cancel the service and the issued invoice will no longer be payable. If the invoice has been paid in advance, it will not be refunded. Treatment of cancellations received after the cancellation deadline are at the discretion of Fintech. Any cancellations after this date (less than 60 days before the scheduled publication date) may be subject to a maximum cancellation fee of 100% of the total cost of the campaign. If the advertising inventory can be replaced with another advertiser, Fintech may reduce or void the cancellation fee, and this will be handled on a case by case basis at the time of cancellation.</span>
      </Section>
      <Section>
        <span>Fintech reserves the right to determine the cancellation fee up to 100% of the total cost of the campaign. Advertising Services cancelled at the request of the advertiser or agency after commencement (publication date) will not be entitled to a refund of the unused portion of the campaign.</span>
      </Section>
      <Headline>3. Confidentiality</Headline>
      <Section>
        <span>
          Where one of us (
          <strong>recipient</strong>
          ) receives Confidential Information from the other party (
          <strong>discloser</strong>
          ) the recipient must: (a) keep the information confidential; (b) not use, disclose or reproduce the information for any purpose other than the purposes of the Agreement; and (c) ensure that its personnel do not do, or omit to do anything, which if done or omitted to be done by it, would breach this clause. The obligations under this clause continue after the end of the Agreement.
        </span>
      </Section>
      <Section>
        <span>The recipient may use or disclose the information to the extent necessary to comply with any law or the requirements of a regulatory body (including a stock exchange), or to obtain professional legal, insurance or accounting advice, or for use in legal proceedings regarding the Agreement.</span>
      </Section>
      <Section>
        <span>This clause does not apply to Confidential Information which: (a) is in or becomes part of the public domain other than through breach of an obligation of confidence; (b) was known to the recipient at the time of disclosure, unless such knowledge arose through breach of an obligation of confidence; (c) was independently developed by the recipient; or (d) is acquired from a third party who was entitled to disclose it.</span>
      </Section>
      <Headline>4. Intellectual Property</Headline>
      <SubHeadline><strong>4.1 Fintech Materials</strong></SubHeadline>
      <Section>
        <span>You acknowledge that: (a) Fintech and/or its licensors retains ownership of all Intellectual Property Rights in the Fintech Materials and all Intellectual Property Rights used or embodied in the software and Website.</span>
      </Section>

      <SubHeadline><strong>4.2 Partner Content</strong></SubHeadline>
      <Section>
        <span>As between us and you, you retain ownership of all Intellectual Property Rights in Partner Content.</span>
      </Section>
      <Section>
        <span>Partner: (a) grants Fintech a non-exclusive, royalty-free, irrevocable, worldwide and transferable right and licence (including the right to sublicense) to use, copy, transmit, store, modify and commercially exploit the Partner Content, and distribute and communicate the Partner Content to the public, via any media channel (and this licence continues indefinitely, unless you delete the Partner Content from our Website as allowed by our policies from time to time); (b) waives any moral rights in the Partner Content; and (c) represents and warrants to Fintech that Partner has all necessary rights to grant the licences and waiver set out in this clause.</span>
      </Section>
      <Section>
        <span>We may use your branding, logo, trade mark and other Intellectual Property in order for us to provide the Advertising Services to you, including on the Website/s for the purposes of helping Users to navigate to the profile for your company or to promote your use of the Website.</span>
      </Section>
      <Headline>5. Liability</Headline>
      <SubHeadline><strong>5.1 Partner’s Compliance</strong></SubHeadline>
      <Section>
        <span>Partner must comply with the restrictions in Schedule 1 and with Relevant Laws.</span>
      </Section>
      <Section>
        <span>During and after the term of the Agreement, the Partner agrees to indemnify Fintech (and its directors, employees and agents) against any claims, damages, losses, liabilities, settlements and expenses (including legal costs on an indemnity basis) resulting from the Partner’s acts or omissions, including any claims based on Partner Content (including Intellectual Property infringement); or any breach of the restrictions in Schedule 1 or Relevant Laws.</span>
      </Section>

      <SubHeadline><strong>5.2 Disclaimer</strong></SubHeadline>
      <Section>
        <span>Partner acknowledges and agrees that Fintech does not guarantee any minimum number of views, clicks, or other audience response to your Content, or any other business or financial outcome from the Advertising Services.</span>
      </Section>
      <Section>
        <span>Any conditions, warranties or other terms which are not stated in the Agreement, or which might be implied or incorporated into the Agreement, whether by statute, common law or otherwise, are hereby excluded to the fullest extent allowed by law, including any implied conditions, warranties or other terms as to satisfactory quality, fitness for purpose or the use of reasonable skill and care.</span>
      </Section>

      <SubHeadline><strong>5.3 Limitation of Liability</strong></SubHeadline>
      <Section>
        <span>To the fullest extent permitted by law and regardless of the form of action, whether in contract, tort including negligence, pursuant to statute or otherwise: (a) the liability of Fintech (and any of its directors, employees, agents or related companies) relating to the supply of the Advertising Services is limited in aggregate to the Fees actually paid by the Partner in the 12 months prior to the act or omission which gave rise to the liability; and (b) Fintech (and any of its directors, employees, agents or related companies) shall have no liability for any down-time, lost profit or product, loss of data, increased cost of production, loss of client goodwill or good standing or delay in operation, lost opportunity or any other special, punitive, economic, indirect or consequential loss or damage.</span>
      </Section>
      <Section>
        <span>Certain rights and remedies may be available under the Competition and Consumer Act 2010 (Cth) and may not be permitted to be excluded, restricted or modified.  To the extent permitted by law, the liability of Fintech for breach of any implied warranty or condition that cannot be excluded is restricted, at Fintech’s option to: (a) the re-supply of services or payment of the cost of re-supply of services; or (b) the replacement or repair of goods or payment of the cost of replacement or repair.</span>
      </Section>

      <SubHeadline><strong>5.4 General</strong></SubHeadline>
      <Section>
        <span>Each party’s liability shall be reduced proportionately to the extent that the other party’s acts or omissions causes or contributes to, directly or indirectly, the loss or damage for which the first party is liable. Each party will not be liable for, or be considered to be in breach of, or default under this agreement, on account of, any delay or failure to perform as required by this agreement (except an obligation to pay the Fees) as a result of any cause or condition beyond that party’s reasonable control.</span>
      </Section>
      <Headline>6. Term & Termination</Headline>
      <Section>
        <span>The Agreement begins on the date the date set out in the Order Form or, if there is no date, the date the Order Form is signed by you and continues for so long as we are providing any Advertising Services to you.</span>
      </Section>
      <Section>
        <span>Either party may terminate the Agreement if the other party: (a) breaches this agreement and does not remedy the breach within one month of written notice; (b) breaches any term of this agreement which cannot be remedied (including a breach of confidentiality or Intellectual Property Rights); or (c) becomes insolvent or bankrupt or has an administrator, receiver or liquidator appointed.</span>
      </Section>
      <Headline>7. General</Headline>
      <Section>
        <span>
          <Underscore>Entire Agreement.</Underscore>
          {' '}
          The Agreement contains everything the parties have agreed in relation to the matters it deals with. These Terms apply to all the Advertising Services we provide to you. You acknowledge that you have not relied on any warranty or representation by us except for the warranties or representations specifically stated in the Agreement.
          {' '}
          <strong>Notwithstanding any acknowledgment by us of a purchase order or other document submitted by you, any term of such purchase order or other document which is inconsistent with, or which adds to the terms of this agreement, is null and void.</strong>
        </span>
      </Section>
      <Section>
        <span>
          <Underscore>Interpretation.</Underscore>
          {' '}
          Words like including, for example, such as or similar expressions are to be interpreted as meaning including, without limitation. If a clause or part of a clause can be read in a way that makes it illegal, unenforceable or invalid, but can also be read in a way that makes it legal, enforceable and valid, it must be read in the latter way. If any clause or part of a clause is illegal, unenforceable or invalid, that clause or part is to be treated as removed from this document, but the rest of this document is not affected.
        </span>
      </Section>
      <Section>
        <span>
          <Underscore>Variation & Waiver.</Underscore>
          {' '}
          A variation of the Agreement is only effective if it is in writing and signed by both of us. The fact that a party fails to do, or delays in doing, something the party is entitled to do under the Agreement, does not amount to a waiver of any obligation of, or breach of obligation by, another party. A waiver by a party is only effective if it is in writing.
        </span>
      </Section>
      <Section>
        <span>
          <Underscore>Assignment.</Underscore>
          {' '}
          Either of us may assign the Agreement as part of any corporate reorganization or sale of business, provided: (i) the assignee agrees in writing to assume all the obligations of the assigning party under the Agreement, and (ii) the assigning party and the assignee give the other party written notice of the assignment.
        </span>
      </Section>
      <Section>
        <span>
          <Underscore>Independent Contractors – not legal ‘partners’.</Underscore>
          {' '}
          The term ‘Partner’ is used in the Agreement to describe a close commercial relationship however the parties expressly agree that they are independent contractors for purposes of this agreement, and not legal partners or agents. No party has authority to bind the other party.
        </span>
      </Section>
      <Section>
        <span>
          <Underscore>Governing law.</Underscore>
          {' '}
          This agreement is governed by the laws of New South Wales, Australia. Each of the parties submits to the non-exclusive jurisdiction of courts with jurisdiction there.
        </span>
      </Section>
      <Headline>8. Definitions</Headline>
      <Section>
        <span>
          <strong>Advertising Services</strong>
          {' '}
          means Fintech publishing Partner Content on its Website and other channels, as specified in the Market Index Insertion Order or otherwise agreed in writing (which may be by email).
        </span>
      </Section>
      <Section>
        <span>
          <strong>Content</strong>
          {' '}
          means any data, text, posts, commentary, images, audio or video material and other content that is uploaded, posted or otherwise transmitted through the Website, or provided in an advertisement by Partner.
        </span>
      </Section>
      <Section>
        <span>
          <strong>Intellectual Property</strong>
          {' '}
          or
          {' '}
          <strong>Intellectual Property</strong>
          {' '}
          Rights means all intellectual property rights (including, without limitation, all registered and unregistered copyright, designs, trade marks and patents) of any nature in any technology, trade secrets, domain names, User lists, User details (including, but not limited to, User emails, telephone numbers and addresses) and User-related information, software, program, inventions, designs, works and subject matter or any rights to registration of such rights existing in Australia or elsewhere and whether created before on or after the date of this agreement.
        </span>
      </Section>
      <Section>
        <span>
          <strong>Fees</strong>
          {' '}
          means the fees payable by the Partner to Fintech as set out in the Market Index Insertion Order.
        </span>
      </Section>
      <Section>
        <span>
          <strong>Fintech or us</strong>
          {' '}
          means Fintech Equity Pty Ltd (ABN 32 637 943 803).
        </span>
      </Section>
      <Section>
        <span>
          <strong>Fintech Content</strong>
          {' '}
          means Content that is created by Fintech and uploaded, posted or otherwise transmitted through the Website by Fintech (other than Content which Fintech posts on behalf of Partner, which is Partner Content).
        </span>
      </Section>
      <Section>
        <span>
          <strong>Fintech Materials</strong>
          {' '}
          means materials created by or on behalf of Fintech and provided to Partner in the course of performing this agreement including the Website, documentation, software, Fintech Content, designs, customisations or modifications, reports about the performance of the Website, and other materials or reports provided as part of the Advertising Services.
        </span>
      </Section>
      <Section>
        <span>
          <strong>Order Form</strong>
          {' '}
          means the order form provided by Livewire to the Partner specifying the details of the Advertising Services including the Fees and Partner Content.
        </span>
      </Section>
      <Section>
        <span>
          <strong>Partner or</strong>
          {' '}
          you means the partner organisation identified in the Order Form.
        </span>
      </Section>
      <Section>
        <span>
          <strong>Partner Content</strong>
          {' '}
          means Content that is provided by you (or developed by us, which you have approved) as part of an Advertising Service.
        </span>
      </Section>
      <Section>
        <span>
          <strong>Relevant Laws</strong>
          {' '}
          means laws, regulations, binding undertakings or orders, or the requirements of any licensing regime which are relevant to Partner’s business, the Website or the creation and publication of financial services information, including: the Competition and Consumer Act, the Fair Trading Acts of relevant States of Australia or equivalent or other sale of goods legislation; any law of defamation, obscenity or contempt of any court, tribunal or royal commission; State or Commonwealth anti-discrimination legislation; the Privacy Act (Cth); Corporations Act 2001, the Australian Securities and Investments Commission Act 2001, ASIC Market Integrity Rules or other ASIC regulatory guides or instruments, Australian Financial Services licence terms, ASX Listing Rules or Operating Rules.
        </span>
      </Section>
      <Section>
        <span>
          <strong>Website</strong>
          {' '}
          means
          {' '}
          <a href="https://www.marketindex.com.au">www.marketindex.com.au</a>
          {' '}
          or other websites as notified by Fintech from time to time.
        </span>
      </Section>
      <Headline>Schedule 1 – Advertising Restrictions</Headline>
      <Section>
        <span>
          You warrant to Fintech that:
          <div>
            <InnerSection>
              <Span10>(a)</Span10>
              if the Partner Content contains the name or photographic or pictorial representation of any living person and/or any copy by which any living person can be identified, the Partner has obtained the authority of that person to make use of his/her name or representation or the copy;
            </InnerSection>
            <InnerSection>
              <Span10>(b)</Span10>
              the Partner Content for any financial products and credit products give a balanced message about the returns, features, benefits and risks associated with the product;

            </InnerSection>
            <InnerSection>
              <Span10>(c)</Span10>
              warnings, disclaimers and qualifications are not inconsistent with other content in the Partner Content, including any headline claims;

            </InnerSection>
            <InnerSection>
              <Span10>(d)</Span10>
              where a fee or cost is referred to in the Partner Content, it gives a realistic impression of the overall level of fees and costs a consumer is likely to pay, including any indirect fees or costs;

            </InnerSection>
            <InnerSection>
              <Span10>(e)</Span10>
              where the Partner Content compares different products, comparisons are made between products that have sufficiently similar features;

            </InnerSection>
            <InnerSection>
              <Span10>(f)</Span10>
              past performance information will be accompanied by a warning that past performance is not indicative of future performance;

            </InnerSection>
            <InnerSection>
              <Span10>(g)</Span10>
              forecasts about the future performance of a financial product are based on reasonable assumptions and will state that the forecasts are not guaranteed to occur;

            </InnerSection>
            <InnerSection>
              <Span10>(h)</Span10>
              where the Partner Content states or implies that a particular benefit is likely, it includes a statement about the risks associated with obtaining that benefit, or about the product generally;

            </InnerSection>
            <InnerSection>
              <Span10>(i)</Span10>
              claims about features of a product or service are made because a consumer can reasonably expect those features to be available to them;

            </InnerSection>
            <InnerSection>
              <Span10>(j)</Span10>
              a Partner Content does not present a one-sided view of a product’s key features to overstate the benefits associated with the product;

            </InnerSection>
            <InnerSection>
              <Span10>(k)</Span10>
              photographs and images do not contradict, detract from or reduce the prominence of any warnings, disclaimers or qualifications;

            </InnerSection>
            <InnerSection>
              <Span10>(l)</Span10>
              the Partner Contents does not overstate the potential benefits or create unrealistic expectations by giving undue prominence to the benefits compared with the risks;

            </InnerSection>
            <InnerSection>
              <Span10>(m)</Span10>
              information about returns and benefits are of net of fees and costs to the greatest extent practicable;

            </InnerSection>
            <InnerSection>
              <Span10>(n)</Span10>
              the effect that fees and costs may have on returns and benefits over time are clearly stated, and not hidden or difficult to understand;

            </InnerSection>
            <InnerSection>
              <Span10>(o)</Span10>
              information about the risks of a product are clear, and not hidden or difficult to understand, and are given sufficient prominence to information about returns and benefits;

            </InnerSection>
            <InnerSection>
              <Span10>(p)</Span10>
              the tone of the Partner Content does not undermine the importance of the risks
            </InnerSection>
            <InnerSection>
              <Span10>(q)</Span10>
              <span>
                the Partner Content:
                <div>
                  <InnerSection>
                    <Span10>(i)</Span10>
                    is not false or misleading;

                  </InnerSection>
                  <InnerSection>
                    <Span10>(ii)</Span10>
                    does not contain a statement that is misleading, false or deceptive;

                  </InnerSection>
                  <InnerSection>
                    <Span10>(iii)</Span10>
                    does not dishonestly conceal facts;

                  </InnerSection>
                  <InnerSection>
                    <Span10>(iv)</Span10>
                    does not make representations about future matters without reasonable grounds;

                  </InnerSection>
                  <InnerSection>
                    <Span10>(v)</Span10>
                    does not contain terms in relation to financial services that are misleading or deceptive or are likely to mislead or deceive;
                  </InnerSection>
                  <InnerSection>
                    <Span10>(vi)</Span10>
                    does not make false representations that are misleading in relation to financial products that involve interests in land; and
                  </InnerSection>
                  <InnerSection>
                    <Span10>(vii)</Span10>
                    does not engage in bait advertising.
                  </InnerSection>
                </div>
              </span>
            </InnerSection>
          </div>
        </span>
      </Section>
    </Container>
  );
}
